import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AlertCircle,
  ChevronDown,
  ClipboardList,
  UserCheck,
  Users,
} from 'lucide-react';
import { NameEmailAvatar } from '../../../../../../../../../ui';
import { QuestionAndAnswer } from '../../Components/QuestionAndAnswer';
import { QuestionCollapsible } from '../../Components/QuestionCollapsible';
import { StarScore } from '../../Components/StartScore';
import { DetailedResultsSkeleton } from './Components/DetailedResultsSkeleton';
import { useDetailedResults } from './Hooks/UseDetailedResults';
import { IDetailedResults } from './Interfaces/Interfaces';

export const DetailedResults: React.FC<IDetailedResults> = ({
  beiResults,
  assessmentData: initialAssessmentData,
  loading,
  error,
}) => {
  const {
    assessmentSections,
    candidateInfo,
    hasAssessmentData,
    hasBeiResults,
    openSections,
    handleSectionToggle,
    detailedQuestions,
    formatTime,
    updateDetailedQuestionArray,
    behavioralSections,
    handleToggleQuestion,
    openQuestions,
  } = useDetailedResults({
    beiResults,
    assessmentData: initialAssessmentData,
    loading,
    error,
  });

  // Handle loading state
  if (loading) {
    return <DetailedResultsSkeleton />;
  }

  // Handle error state
  if (error) {
    return (
      <Box sx={{ width: '100%', minHeight: 400, p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: 1,
            borderColor: 'error.light',
            bgcolor: 'error.50',
            textAlign: 'center',
            borderRadius: 2,
            maxWidth: 500,
            mx: 'auto',
          }}
        >
          <Stack spacing={2} alignItems="center">
            <AlertCircle size={32} color="#dc2626" />
            <Typography variant="h6" fontWeight={700} color="error.dark">
              Error Loading Results
            </Typography>
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
            <Button
              variant="contained"
              color="error"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (!hasBeiResults && !hasAssessmentData) {
    return (
      <Box sx={{ width: '100%', minHeight: 400, p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            maxWidth: 500,
            mx: 'auto',
          }}
        >
          <Stack spacing={2} alignItems="center">
            <ClipboardList size={48} color="#94a3b8" />
            <Typography variant="h6" fontWeight={600} color="text.primary">
              No Detailed Results Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No assessment data could be loaded at this time.
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'warning.50',
                border: 1,
                borderColor: 'warning.light',
                borderRadius: 1.5,
                textAlign: 'left',
                width: '100%',
              }}
            >
              <Typography variant="caption" fontWeight={700} color="warning.dark" display="block" mb={1}>
                Possible reasons:
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="warning.dark">
                  • Assessment is still in progress
                </Typography>
                <Typography variant="caption" color="warning.dark">
                  • Technical assessment data is not available
                </Typography>
                <Typography variant="caption" color="warning.dark">
                  • Behavioral interview has not been completed
                </Typography>
                <Typography variant="caption" color="warning.dark">
                  • Server error occurred while loading data
                </Typography>
              </Stack>
            </Paper>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ mt: 1 }}
            >
              Refresh Page
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 75) return 'primary.main';
    if (score >= 50) return 'info.main';
    if (score >= 25) return 'warning.main';
    return 'error.main';
  };

  return (
    <Box sx={{ width: '100%', minHeight: 'calc(100vh - 8rem)', overflowY: 'auto' }}>
      <Box sx={{ pb: 6 }}>
        {/* Candidate Info Header */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <NameEmailAvatar
              name={candidateInfo.name}
              email={candidateInfo.email}
              avatar=""
            />
            <Box>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {candidateInfo.company}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {candidateInfo.date}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Skills Assessment Section */}
        {assessmentSections.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'background.paper',
                borderLeft: 4,
                borderLeftColor: 'primary.main',
                border: 1,
                borderColor: 'divider',
                borderLeftWidth: 4,
                borderRadius: 1.5,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <UserCheck size={16} color="#2563eb" />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase">
                  Skills Assessment
                </Typography>
              </Stack>
            </Paper>

            <Grid container spacing={2}>
              {assessmentSections.map((section) => {
                const isExpanded = openSections.has(section.title);
                return (
                  <Grid
                    item
                    xs={12}
                    md={isExpanded ? 12 : 6}
                    lg={isExpanded ? 12 : 4}
                    key={section.title}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        border: 1,
                        borderColor: isExpanded ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {/* Section Header */}
                      <Box
                        onClick={() => handleSectionToggle(section.title)}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          bgcolor: isExpanded ? 'action.hover' : 'background.paper',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={2}
                          mb={1.5}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700} noWrap>
                              {section.title}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                {section.totalQuestions} Questions
                              </Typography>
                              {section.followUpQuestions > 0 && (
                                <Typography variant="caption" color="warning.main">
                                  • {section.followUpQuestions} Follow-ups
                                </Typography>
                              )}
                            </Stack>
                          </Box>

                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              color={getScoreColor(section.score)}
                            >
                              {section.score}%
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                transform: isExpanded ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s ease',
                              }}
                            >
                              <ChevronDown size={20} color="#94a3b8" />
                            </Box>
                          </Stack>
                        </Stack>

                        {/* Quick Stats Grid */}
                        <Grid container spacing={1} sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Correct
                            </Typography>
                            <Typography variant="caption" fontWeight={700}>
                              {
                                detailedQuestions.filter(
                                  (q) =>
                                    q.skillId === section.skillId &&
                                    q.isCorrect,
                                ).length
                              }
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Accuracy
                            </Typography>
                            <Typography variant="caption" fontWeight={700} color="primary.main">
                              {Math.round(
                                (detailedQuestions.filter(
                                  (q) =>
                                    q.skillId === section.skillId &&
                                    q.isCorrect,
                                ).length /
                                  (detailedQuestions.filter(
                                    (q) => q.skillId === section.skillId,
                                  ).length || 1)) *
                                  100,
                              )}%
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Total
                            </Typography>
                            <Typography variant="caption" fontWeight={700}>
                              {
                                detailedQuestions.filter(
                                  (q) => q.skillId === section.skillId,
                                ).length
                              }
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Avg Time
                            </Typography>
                            <Typography variant="caption" fontWeight={700}>
                              {(() => {
                                const questionsForSkill =
                                  detailedQuestions.filter(
                                    (q) => q.skillId === section.skillId,
                                  );
                                const totalSeconds = questionsForSkill.reduce(
                                  (sum, q) => sum + q.timeTaken,
                                  0,
                                );
                                const avgSeconds =
                                  questionsForSkill.length > 0
                                    ? Math.round(
                                      totalSeconds / questionsForSkill.length,
                                    )
                                    : 0;
                                return formatTime(avgSeconds);
                              })()}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Expanded Questions */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ p: 2.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="text.secondary"
                              textTransform="uppercase"
                            >
                              Questions & Answers
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {
                                detailedQuestions.filter(
                                  (q) => q.skillId === section.skillId,
                                ).length
                              }{' '}
                              question(s)
                            </Typography>
                          </Stack>
                          <Stack spacing={1.5}>
                            {detailedQuestions
                              .filter((q) => q.skillId === section.skillId)
                              .map((question) => (
                                <QuestionCollapsible
                                  key={question.id}
                                  assessmentQuestionResult={question}
                                  updateDetailedQuestionArray={
                                    updateDetailedQuestionArray
                                  }
                                  showSystemAnswer
                                />
                              ))}
                          </Stack>
                        </Box>
                      </Collapse>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Behavioral Interview Section */}
        {behavioralSections.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'background.paper',
                borderLeft: 4,
                borderLeftColor: 'success.main',
                border: 1,
                borderColor: 'divider',
                borderLeftWidth: 4,
                borderRadius: 1.5,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: 'success.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Users size={16} color="#16a34a" />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase">
                  Behavioral Interview (BEI)
                </Typography>
              </Stack>
            </Paper>

            <Grid container spacing={2}>
              {behavioralSections.map((section) => {
                const isExpanded = openSections.has(section.title);
                return (
                  <Grid
                    item
                    xs={12}
                    md={isExpanded ? 12 : 6}
                    lg={isExpanded ? 12 : 4}
                    key={section.title}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        border: 1,
                        borderColor: isExpanded ? 'success.main' : 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {/* Section Header */}
                      <Box
                        onClick={() => handleSectionToggle(section.title)}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          bgcolor: isExpanded ? 'action.hover' : 'background.paper',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={2}
                          mb={1.5}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700} noWrap>
                              {section.title}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                {section.totalQuestions} Questions
                              </Typography>
                              {section.followUpQuestions > 0 && (
                                <Typography variant="caption" color="warning.main">
                                  • {section.followUpQuestions} Follow-ups
                                </Typography>
                              )}
                            </Stack>
                          </Box>

                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              color={getScoreColor(section.score)}
                            >
                              {section.score}%
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                transform: isExpanded ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s ease',
                              }}
                            >
                              <ChevronDown size={20} color="#94a3b8" />
                            </Box>
                          </Stack>
                        </Stack>

                        {/* STAR Breakdown */}
                        {section.starBreakdown && (
                          <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                            <StarScore breakdown={section.starBreakdown} />
                          </Box>
                        )}
                      </Box>

                      {/* Expanded Questions */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ p: 2.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="text.secondary"
                              textTransform="uppercase"
                            >
                              Questions & Responses
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {openQuestions.size > 0
                                ? `${openQuestions.size} expanded`
                                : `${section.questions.length} question(s)`}
                            </Typography>
                          </Stack>
                          <Stack spacing={1.5}>
                            {section.questions.map((q, idx) => {
                              const questionId = `${section.title}-${idx}`;
                              return (
                                <Paper
                                  key={`${section.title}-${idx}-${q.videoResponse}`}
                                  elevation={0}
                                  sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <QuestionAndAnswer
                                    starBreakdown={q.starBreakdown}
                                    followUpQuestions={q.followUpQuestions}
                                    handleOpenQuestion={() =>
                                      handleToggleQuestion(questionId)
                                    }
                                    openQuestion={
                                      openQuestions.has(questionId)
                                        ? q.question
                                        : null
                                    }
                                    question={q.question}
                                    response={q.response}
                                    score={q.score}
                                    skillAttribute={q.skillAttribute}
                                    timeTaken={q.timeTaken}
                                    videoResponse={q.videoResponse}
                                  />
                                </Paper>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Collapse>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
};
